import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { addDays, describeAmount, recipeSlotOf, scheduledOn } from '@kraftwerk/shared';
import type { DaySlot } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import type { LocalFood, LocalRecipe } from '../../db/local.js';
import {
  listFoods,
  listPlannedMeals,
  listRecipes,
  planMeal,
  removePlannedMeal,
} from '../../db/nutrition.js';
import { formatDay } from '../../format.js';
import { readProfile } from '../../db/profile.js';
import { listSupplements } from '../../db/supplements.js';
import { toNumber } from './FoodForm.js';
import { plannedName } from './DayLog.js';

/**
 * Der Wochenplan: sieben Tage, dieselben Slots wie im Tagesprotokoll.
 *
 * Geplant zählt als gegessen (18.10.2026): Jeder Planeintrag steht als Zeile
 * im Tagesprotokoll (`plannedLogId`), markiert wird im Tag nur, was
 * ausgelassen wurde — wie bei allem anderen, was man vorab festlegt. Vorher
 * musste jeder Eintrag im Tag einzeln „gegessen“ werden.
 */
export function WeekPlan({ startDate }: { startDate: string }): React.JSX.Element {
  const { t } = useTranslation();
  const days = Array.from({ length: 7 }, (_, index) => addDays(startDate, index));

  return (
    <section aria-labelledby="weekplan-heading" className="flex flex-col gap-4">
      <h2 id="weekplan-heading" className="text-lg font-semibold">
        {t('nutrition.week.heading')}
      </h2>
      <p className="text-sm text-ink-muted">{t('nutrition.week.intro')}</p>

      <ol className="flex flex-col gap-3">
        {days.map((date) => (
          <li key={date}>
            <DayColumn date={date} />
          </li>
        ))}
      </ol>
    </section>
  );
}

function DayColumn({ date }: { date: string }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [adding, setAdding] = useState(false);

  const entries = useLiveQuery(() => listPlannedMeals(date, db), [date], []);
  const recipes = useLiveQuery(() => listRecipes(db), [], [] as LocalRecipe[]);
  const byId = new Map(recipes.map((recipe) => [recipe.id, recipe]));
  const foods = useLiveQuery(() => listFoods(db), [], [] as LocalFood[]);
  const foodById = new Map(foods.map((food) => [food.id, food]));
  // Supplements nach ihrem Plan (Wochentage, Monate) — mit Menge.
  const supplements = useLiveQuery(() => listSupplements(db), [], []).filter(
    (entry) =>
      entry.active &&
      scheduledOn(
        { times: entry.scheduleTimes, weekdays: entry.weekdays, months: entry.months },
        date,
      ),
  );
  const language = i18n.language.startsWith('en') ? 'en' : 'de';

  const weekday = formatDay(date, 'long', i18n.language);

  return (
    <div className="kw-tile">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-medium">{weekday}</h3>
        <button
          type="button"
          onClick={() => setAdding((value) => !value)}
          aria-expanded={adding}
          className="kw-btn kw-btn-quiet kw-btn-small px-3"
        >
          {t('nutrition.week.add')}
        </button>
      </div>

      {entries.length === 0 ? (
        <p className="mt-2 text-sm text-ink-muted">{t('nutrition.week.empty')}</p>
      ) : (
        <ul className="mt-2 flex flex-col gap-1">
          {entries.map((entry) => {
            const name = plannedName(
              entry,
              entry.recipeId ? byId.get(entry.recipeId) : undefined,
              entry.foodId ? foodById.get(entry.foodId) : undefined,
              t,
            );
            return (
              <li key={entry.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="min-w-0 truncate">
                  <span className="text-ink-muted">{t(`nutrition.slot.${entry.slot}`)}: </span>
                  {name}
                </span>
                <button
                  type="button"
                  onClick={() => void removePlannedMeal(entry.id, db)}
                  aria-label={t('nutrition.day.unplanNamed', { name })}
                  className="shrink-0 kw-btn kw-btn-quiet kw-btn-small px-3 hover:border-danger hover:text-danger"
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {supplements.length > 0 ? (
        <p className="mt-2 text-xs text-ink-muted">
          {t('nutrition.week.supplements', {
            list: supplements
              .map((entry) =>
                [entry.name, describeAmount(entry.dose, entry.unit, language)]
                  .filter(Boolean)
                  .join(' '),
              )
              .join(', '),
          })}
        </p>
      ) : null}

      {adding ? (
        <PlanForm date={date} recipes={recipes} foods={foods} onDone={() => setAdding(false)} />
      ) : null}
    </div>
  );
}

function PlanForm({
  date,
  recipes,
  foods,
  onDone,
}: {
  date: string;
  recipes: LocalRecipe[];
  foods: LocalFood[];
  onDone: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const slotId = useId();
  const recipeId = useId();
  const foodId = useId();
  const gramsId = useId();
  const [kind, setKind] = useState<'recipe' | 'food'>('recipe');
  const [slot, setSlot] = useState<DaySlot>('lunch');
  // Dieselben Mahlzeiten wie im Tag — in deiner Reihenfolge, mit weiteren Snacks.
  const meals = useLiveQuery(async () => (await readProfile(db)).meals, [], []);
  const [recipe, setRecipe] = useState('');
  const [food, setFood] = useState('');
  const [grams, setGrams] = useState('100');

  return (
    <form
      className="mt-3 flex flex-wrap items-end gap-2 rounded-lg border border-accent bg-surface-raised p-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (kind === 'recipe') {
          if (!recipe) return;
          void planMeal({ date, slot, recipeId: recipe }, db).then(onDone);
          return;
        }
        const amount = toNumber(grams);
        if (!food || amount === null) return;
        void planMeal({ date, slot, foodId: food, grams: amount }, db).then(onDone);
      }}
    >
      <div className="flex w-full gap-1">
        {(['recipe', 'food'] as const).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={kind === option}
            onClick={() => setKind(option)}
            className={`kw-btn kw-btn-small flex-1 ${kind === option ? '' : 'kw-btn-quiet text-ink'}`}
          >
            {t(`nutrition.day.mode.${option}`)}
          </button>
        ))}
      </div>
      <label htmlFor={slotId} className="flex flex-col gap-1 text-xs text-ink-muted">
        {t('nutrition.recipe.slot')}
        <select
          id={slotId}
          value={slot}
          onChange={(event) => setSlot(event.target.value as DaySlot)}
          className="kw-field text-ink"
        >
          {meals.map((meal) => (
            <option key={meal.slot} value={meal.slot}>
              {t(`nutrition.slot.${meal.slot}`)}
            </option>
          ))}
        </select>
      </label>

      {kind === 'food' ? (
        <>
          <label
            htmlFor={foodId}
            className="flex min-w-40 flex-1 flex-col gap-1 text-xs text-ink-muted"
          >
            {t('nutrition.day.pickFood')}
            <select
              id={foodId}
              value={food}
              onChange={(event) => setFood(event.target.value)}
              className="kw-field text-ink"
            >
              <option value="">{t('nutrition.day.choose')}</option>
              {foods.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor={gramsId} className="flex w-24 flex-col gap-1 text-xs text-ink-muted">
            {t('nutrition.recipe.grams')}
            <input
              id={gramsId}
              inputMode="decimal"
              value={grams}
              onChange={(event) => setGrams(event.target.value)}
              className="kw-field text-ink"
            />
          </label>
        </>
      ) : null}

      {kind === 'recipe' ? (
        <label
          htmlFor={recipeId}
          className="flex min-w-40 flex-1 flex-col gap-1 text-xs text-ink-muted"
        >
          {t('nutrition.day.pickRecipe')}
          <select
            id={recipeId}
            value={recipe}
            onChange={(event) => setRecipe(event.target.value)}
            className="kw-field text-ink"
          >
            <option value="">{t('nutrition.day.choose')}</option>
            {/*
            Gerichte des Slots stehen oben — aber die anderen bleiben sichtbar.
            Ein Filter, der sie ausblendet, entscheidet für den Nutzer, dass es
            die Reste von gestern mittags nicht geben darf.
          */}
            <optgroup label={t(`nutrition.slot.${recipeSlotOf(slot)}`)}>
              {recipes
                .filter((entry) => entry.slot === recipeSlotOf(slot))
                .map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name}
                  </option>
                ))}
            </optgroup>
            <optgroup label={t('nutrition.week.otherSlots')}>
              {recipes
                .filter((entry) => entry.slot !== recipeSlotOf(slot))
                .map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name}
                  </option>
                ))}
            </optgroup>
          </select>
        </label>
      ) : null}

      <button type="submit" className="kw-btn kw-btn-small">
        {t('nutrition.week.plan')}
      </button>
    </form>
  );
}
